import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePontoStatus } from '@/features/ponto/hooks/usePonto';
import {
  pontoApi,
  type BatidaResumo,
  type ColaboradorPontoStatus,
  type TipoBatida,
} from '@/features/ponto/api/pontoApi';
import { useColaboradores } from '@/features/colaboradores/hooks/useColaboradores';
import { useRotaEntidade } from '@/app/useRotaEntidade';
import { ROTAS } from '@/app/rotas';
import { BadgeSyncPendente } from '@/shared/ui/BadgeSyncPendente';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Paginacao } from '@/shared/ui/Paginacao';
import { Toast, type TipoToast } from '@/shared/ui/Toast';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { usePaginacao } from '@/shared/hooks/usePaginacao';
import { useSyncReload } from '@/shared/hooks/useSyncReload';
import { exportarCsv, imprimirElemento } from '@/shared/format/exportar';

const fmtDataHora = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const fmtHora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
const fmtDataLonga = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function formatarMinutos(min: number): string {
  if (min <= 0) return '0min';
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${String(m).padStart(2, '0')}min`;
}

/** ISO → valor de <input type="datetime-local"> no horário local (YYYY-MM-DDTHH:MM). */
function isoParaInputLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function agoraInputLocal(): string {
  return isoParaInputLocal(new Date().toISOString());
}

function inputLocalParaIso(valor: string): string {
  return new Date(valor).toISOString();
}

function hojeData(): string {
  return new Date().toISOString().slice(0, 10);
}

function inicioMesData(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

type ModoModal =
  | { tipo: 'fechado' }
  | { tipo: 'novo' }
  | { tipo: 'editar'; batida: BatidaResumo };

export function PontoPage() {
  const { status, carregando, erro, recarregar } = usePontoStatus();
  const { colaboradores } = useColaboradores();
  const dialogos = useDialogos();

  const [toast, setToast] = useState<{ mensagem: string; tipo: TipoToast } | null>(null);
  const [batendoId, setBatendoId] = useState<string | null>(null);
  // `/ponto/batidas/nova` e `/ponto/batidas/:id/editar`.
  const rota = useRotaEntidade(`${ROTAS.ponto}/batidas`);

  // Filtro do histórico
  const [filtroColaborador, setFiltroColaborador] = useState('');
  const [inicio, setInicio] = useState(inicioMesData);
  const [fim, setFim] = useState(hojeData);
  const [batidas, setBatidas] = useState<BatidaResumo[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);

  const modo = useMemo<ModoModal>(() => {
    if (rota.modo === 'novo') return { tipo: 'novo' };
    if (rota.modo === 'editar') {
      const batida = batidas.find((b) => b.id === rota.id);
      return batida ? { tipo: 'editar', batida } : { tipo: 'fechado' };
    }
    return { tipo: 'fechado' };
  }, [rota, batidas]);
  const historicoPaginado = usePaginacao(batidas, 25);

  const carregarHistorico = useCallback(() => {
    setCarregandoHistorico(true);
    return pontoApi
      .listarBatidas({ colaboradorId: filtroColaborador || undefined, inicio, fim })
      .then(
        (b) => setBatidas(b),
        (e: unknown) =>
          setToast({
            mensagem: e instanceof Error ? e.message : 'Falha ao carregar histórico.',
            tipo: 'erro',
          }),
      )
      .finally(() => setCarregandoHistorico(false));
  }, [filtroColaborador, inicio, fim]);

  useEffect(() => {
    void carregarHistorico();
  }, [carregarHistorico]);

  // Quando o push confirma (badge "Aguardando servidor" deve sumir) o
  // histórico recarrega sozinho, sem o usuário precisar mexer no filtro.
  useSyncReload(carregarHistorico);

  async function baterPonto(c: ColaboradorPontoStatus) {
    setBatendoId(c.colaboradorId);
    try {
      const r = await pontoApi.bater(c.colaboradorId);
      setToast({
        mensagem: `${r.tipo === 'Entrada' ? 'Entrada' : 'Saída'} registrada para ${c.nome}.`,
        tipo: 'sucesso',
      });
      await Promise.all([recarregar(), carregarHistorico()]);
    } catch (e) {
      setToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao registrar ponto.',
        tipo: 'erro',
      });
    } finally {
      setBatendoId(null);
    }
  }

  async function excluirBatida(b: BatidaResumo) {
    const ok = await dialogos.confirmar({
      titulo: 'Excluir batida',
      mensagem: `Excluir a batida de ${b.tipo === 'Entrada' ? 'entrada' : 'saída'} de ${
        b.colaboradorNome
      } em ${fmtDataHora.format(new Date(b.momento))}?`,
      textoConfirmar: 'Excluir',
      perigoso: true,
    });
    if (!ok) return;
    try {
      await pontoApi.excluir(b.id);
      setToast({ mensagem: 'Batida excluída.', tipo: 'sucesso' });
      await Promise.all([recarregar(), carregarHistorico()]);
    } catch (e) {
      setToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao excluir.',
        tipo: 'erro',
      });
    }
  }

  function exportar() {
    if (batidas.length === 0) {
      setToast({ mensagem: 'Nada para exportar no período.', tipo: 'erro' });
      return;
    }
    exportarCsv(`ponto_${inicio}_a_${fim}`, [
      {
        titulo: `Controle de ponto — ${fmtDataLonga.format(new Date(inicio))} a ${fmtDataLonga.format(
          new Date(fim),
        )}`,
        cabecalho: ['Colaborador', 'Data', 'Hora', 'Tipo', 'Origem', 'Observação'],
        linhas: batidas.map((b) => {
          const d = new Date(b.momento);
          return [
            b.colaboradorNome,
            fmtDataLonga.format(d),
            fmtHora.format(d),
            b.tipo === 'Entrada' ? 'Entrada' : 'Saída',
            b.origem,
            b.observacao ?? '',
          ];
        }),
      },
    ]);
  }

  return (
    <section className="page">
      <PageHeader
        titulo="Controle de ponto"
        descricao="Registre a entrada e a saída dos colaboradores. As horas são calculadas automaticamente."
      />

      <Toast
        mensagem={toast?.mensagem ?? null}
        tipo={toast?.tipo}
        onFechar={() => setToast(null)}
      />

      {erro && (
        <p role="alert" className="alert alert--error">
          {erro}
        </p>
      )}

      {/* ---------- Bater ponto ---------- */}
      <div className="card">
        <div className="card__header">
          <span className="card__title">Bater ponto</span>
        </div>
        <div className="card__body">
          {carregando && status.length === 0 ? (
            <p className="hint">Carregando…</p>
          ) : status.length === 0 ? (
            <EmptyState
              titulo="Nenhum colaborador ativo."
              descricao="Cadastre colaboradores (ativos) para registrar o ponto."
            />
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 12,
              }}
            >
              {status.map((c) => (
                <div
                  key={c.colaboradorId}
                  className="card"
                  style={{ margin: 0, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ display: 'block' }}>{c.nome}</strong>
                      {c.cargo && (
                        <span className="hint" style={{ fontSize: 12 }}>
                          {c.cargo}
                        </span>
                      )}
                    </div>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        color: c.trabalhando ? 'var(--color-success)' : 'var(--color-text-subtle)',
                      }}
                    >
                      {c.trabalhando && c.desde
                        ? `● desde ${fmtHora.format(new Date(c.desde))}`
                        : '○ Fora'}
                    </span>
                  </div>
                  <div className="hint" style={{ fontSize: 12 }}>
                    Hoje: <strong>{formatarMinutos(c.minutosHoje)}</strong>
                  </div>
                  <Button
                    variante={c.trabalhando ? undefined : 'primary'}
                    onClick={() => void baterPonto(c)}
                    disabled={batendoId === c.colaboradorId}
                  >
                    {batendoId === c.colaboradorId
                      ? 'Registrando…'
                      : c.trabalhando
                        ? 'Registrar saída'
                        : 'Registrar entrada'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---------- Histórico ---------- */}
      <div className="card">
        <div className="card__header">
          <span className="card__title">Histórico de batidas</span>
        </div>
        <div className="card__body">
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              marginBottom: 12,
            }}
          >
            <Field rotulo="Colaborador">
              {(id) => (
                <select
                  id={id}
                  className="input"
                  value={filtroColaborador}
                  onChange={(e) => setFiltroColaborador(e.target.value)}
                >
                  <option value="">Todos</option>
                  {colaboradores.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field rotulo="De">
              {(id) => (
                <input
                  id={id}
                  type="date"
                  className="input"
                  value={inicio}
                  onChange={(e) => setInicio(e.target.value)}
                />
              )}
            </Field>
            <Field rotulo="Até">
              {(id) => (
                <input
                  id={id}
                  type="date"
                  className="input"
                  value={fim}
                  onChange={(e) => setFim(e.target.value)}
                />
              )}
            </Field>
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <Button onClick={exportar}>Exportar CSV</Button>
              <Button onClick={() => imprimirElemento('#ponto-historico')}>Imprimir</Button>
              <Button variante="primary" onClick={() => rota.abrir('nova')}>
                + Adicionar batida
              </Button>
            </div>
          </div>

          <div id="ponto-historico">
            {carregandoHistorico && batidas.length === 0 ? (
              <p className="hint">Carregando…</p>
            ) : batidas.length === 0 ? (
              <p className="empty-state">Nenhuma batida no período.</p>
            ) : (
              <div className="table-scroll">
                <table className="table table--responsive">
                  <thead>
                    <tr>
                      <th>Colaborador</th>
                      <th>Data e hora</th>
                      <th>Tipo</th>
                      <th>Origem</th>
                      <th>Observação</th>
                      <th aria-label="Ações" />
                    </tr>
                  </thead>
                  <tbody>
                    {historicoPaginado.paginados.map((b) => (
                      <tr key={b.id}>
                        <td className="table__primary">
                          {b.colaboradorNome} <BadgeSyncPendente pendente={b.syncPendente} />
                        </td>
                        <td data-label="Data e hora">{fmtDataHora.format(new Date(b.momento))}</td>
                        <td data-label="Tipo">{b.tipo === 'Entrada' ? 'Entrada' : 'Saída'}</td>
                        <td data-label="Origem">{b.origem === 'Manual' ? 'Manual' : 'Normal'}</td>
                        <td data-label="Observação">{b.observacao ?? '—'}</td>
                        <td className="row-actions" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <Button onClick={() => rota.abrir(`${b.id}/editar`)}>
                            Editar
                          </Button>{' '}
                          <Button onClick={() => void excluirBatida(b)}>Excluir</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <Paginacao
            pagina={historicoPaginado.pagina}
            totalPaginas={historicoPaginado.totalPaginas}
            total={historicoPaginado.total}
            tamanhoPagina={historicoPaginado.tamanhoPagina}
            onMudarPagina={historicoPaginado.setPagina}
          />
        </div>
      </div>

      {modo.tipo !== 'fechado' && (
        <BatidaFormModal
          modo={modo}
          colaboradores={colaboradores.map((c) => ({ id: c.id, nome: c.nome }))}
          onFechar={rota.fechar}
          onSalvo={() => {
            rota.fechar();
            setToast({ mensagem: 'Batida salva.', tipo: 'sucesso' });
            void recarregar();
            void carregarHistorico();
          }}
          onErro={(m) => setToast({ mensagem: m, tipo: 'erro' })}
        />
      )}
    </section>
  );
}

function BatidaFormModal({
  modo,
  colaboradores,
  onFechar,
  onSalvo,
  onErro,
}: {
  modo: { tipo: 'novo' } | { tipo: 'editar'; batida: BatidaResumo };
  colaboradores: Array<{ id: string; nome: string }>;
  onFechar: () => void;
  onSalvo: () => void;
  onErro: (mensagem: string) => void;
}) {
  const ehEdicao = modo.tipo === 'editar';
  const [colaboradorId, setColaboradorId] = useState(
    ehEdicao ? modo.batida.colaboradorId : colaboradores[0]?.id ?? '',
  );
  const [tipo, setTipo] = useState<TipoBatida>(ehEdicao ? modo.batida.tipo : 'Entrada');
  const [momento, setMomento] = useState(
    ehEdicao ? isoParaInputLocal(modo.batida.momento) : agoraInputLocal(),
  );
  const [observacao, setObservacao] = useState(ehEdicao ? modo.batida.observacao ?? '' : '');
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    if (!colaboradorId) {
      onErro('Selecione um colaborador.');
      return;
    }
    if (!momento) {
      onErro('Informe a data e hora.');
      return;
    }
    setSalvando(true);
    try {
      const iso = inputLocalParaIso(momento);
      const obs = observacao.trim() || null;
      if (ehEdicao) {
        await pontoApi.atualizar(modo.batida.id, { tipo, momento: iso, observacao: obs });
      } else {
        await pontoApi.criar({ colaboradorId, tipo, momento: iso, observacao: obs });
      }
      onSalvo();
    } catch (e) {
      onErro(e instanceof Error ? e.message : 'Falha ao salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto
      titulo={ehEdicao ? 'Editar batida' : 'Adicionar batida'}
      onFechar={onFechar}
      rodape={
        <>
          <Button onClick={onFechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button variante="primary" onClick={() => void salvar()} disabled={salvando}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field rotulo="Colaborador" obrigatorio>
          {(id) => (
            <select
              id={id}
              className="input"
              value={colaboradorId}
              onChange={(e) => setColaboradorId(e.target.value)}
              disabled={ehEdicao}
            >
              {colaboradores.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field rotulo="Tipo">
          {(id) => (
            <select
              id={id}
              className="input"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoBatida)}
            >
              <option value="Entrada">Entrada</option>
              <option value="Saida">Saída</option>
            </select>
          )}
        </Field>
        <Field rotulo="Data e hora" obrigatorio>
          {(id) => (
            <input
              id={id}
              type="datetime-local"
              className="input"
              value={momento}
              onChange={(e) => setMomento(e.target.value)}
            />
          )}
        </Field>
        <Field rotulo="Observação" dica="Opcional. Ex.: ajuste de batida esquecida.">
          {(id) => (
            <input
              id={id}
              className="input"
              value={observacao}
              maxLength={500}
              onChange={(e) => setObservacao(e.target.value)}
            />
          )}
        </Field>
      </div>
    </Modal>
  );
}
